import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client/react';
import {
  GroupType,
  ProjectAnnotationStateQuery,
  ProjectAnnotationStateQueryVariables,
  useProjectCreateGroupMutation,

  useProjectMoveAnnotationsToGroupMutation,
  useProjectUngroupMutation,
  useProjectUpdateOrderingMutation,
} from '../../types/graphqlTypes';
import gql from 'graphql-tag';
import { PROJECT_ANNOTATION_STATE } from '../../graphql/project';
import { trackPendingStateMutation } from './annotationMutationCoalescer';
import { appendToProjectState, groupTypenameInCache, identifyProject, nextOrderedIndex, ORDERED_ENTITY_FRAGMENT } from './optimisticProjectState';

/** Map GroupType to the correct GraphQL __typename for the group union. */
function groupTypeName(type?: GroupType | null): 'AnnotationGroup' | 'Floor' | 'SurfaceGroup' | 'RoomGroup' {
  if (type === GroupType.Floor) return 'Floor';
  if (type === GroupType.Surface) return 'SurfaceGroup';
  if (type === GroupType.Room) return 'RoomGroup';
  return 'AnnotationGroup';
}

// Fragments must name a concrete type — one per Group implementer.
const GROUP_ENTITY_FRAGMENTS: Record<ReturnType<typeof groupTypeName>, ReturnType<typeof gql>> = {
  AnnotationGroup: gql`
    fragment NewAnnotationGroupEntity on AnnotationGroup {
      identifier
      name
      type
      parentIdentifier
    }
  `,
  Floor: gql`
    fragment NewFloorEntity on Floor {
      identifier
      name
      type
      parentIdentifier
    }
  `,
  SurfaceGroup: gql`
    fragment NewSurfaceGroupEntity on SurfaceGroup {
      identifier
      name
      type
      parentIdentifier
    }
  `,
  RoomGroup: gql`
    fragment NewRoomGroupEntity on RoomGroup {
      identifier
      name
      type
      parentIdentifier
    }
  `,
};

/** Read the annotation state cache for a project, or null if not cached. */
function readCache(apolloClient: ReturnType<typeof useApolloClient>, projectId: string) {
  try {
    return apolloClient.readQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
      query: PROJECT_ANNOTATION_STATE,
      variables: { projectId },
    });
  } catch {
    return null;
  }
}

/** Write an updated annotation state to the Apollo cache. */
function writeCache(
  apolloClient: ReturnType<typeof useApolloClient>,
  projectId: string,
  cached: NonNullable<ReturnType<typeof readCache>>,
  stateUpdater: (state: NonNullable<ReturnType<typeof readCache>>['projectById']['state']) => Partial<NonNullable<ReturnType<typeof readCache>>['projectById']['state']>,
) {
  const currentState = cached.projectById.state;
  apolloClient.writeQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
    query: PROJECT_ANNOTATION_STATE,
    variables: { projectId },
    data: {
      ...cached,
      projectById: {
        ...cached.projectById,
        state: {
          ...currentState,
          ...stateUpdater(currentState),
        },
      },
    },
  });
}

const GROUP_RENAME_FRAGMENT = gql`
  fragment GroupRename on Group {
    identifier
    name
  }
`;

/**
 * Optimistically rename a group in the annotation-state cache so the left
 * panel and viewport labels update instantly; the rename mutation's full-state
 * response reconciles afterwards. Client-level writeFragment both updates the
 * entity and fires the query broadcast.
 */
export function renameGroupInCache(apolloClient: ReturnType<typeof useApolloClient>, projectId: string, identifier: string, name: string): void {
  try {
    const projectRef = identifyProject(apolloClient.cache, projectId);
    if (!projectRef) return;
    const typename = groupTypenameInCache(apolloClient.cache, projectRef, identifier);
    if (!typename) return; // group not in cache — the mutation response reconciles
    apolloClient.writeFragment({ data: { __typename: typename, identifier, name }, fragment: GROUP_RENAME_FRAGMENT });
  } catch (err) {
    console.error('[renameGroup] optimistic cache update failed:', err);
  }
}

export function useGroupOperations() {
  const { projectId } = useParams<{ projectId: string }>();
  const apolloClient = useApolloClient();
  const [createGroupMutation] = useProjectCreateGroupMutation();
  const [moveAnnotationsToGroupMutation] = useProjectMoveAnnotationsToGroupMutation();
  const [ungroupMutation] = useProjectUngroupMutation();
  const [updateOrderingMutation] = useProjectUpdateOrderingMutation();

  /**
   * A failed tracked mutation clears its registry protection but leaves the
   * optimistic write in the cache (and the groupIdentifier read policy needs a
   * STORE change to invalidate its cached result) — restore server truth.
   */
  const refetchAnnotationState = useCallback(
    async (projectId: string) => {
      await apolloClient
        .query({ query: PROJECT_ANNOTATION_STATE, variables: { projectId }, fetchPolicy: 'network-only' })
        .catch(() => undefined);
    },
    [apolloClient],
  );

  const createGroup = useCallback(
    async (args: { identifier: string; name: string; type?: GroupType; annotationIdentifiers: string[]; parentIdentifier?: string | null }) => {
      if (!projectId) return;

      // Optimistic cache update: add group and move annotations into it.
      // Uses the robust fragment/modify pattern (appendToProjectState dedupes,
      // so a retried failed mutation can't stack a second copy) and finishes
      // with a client-level write so active queries re-render immediately.
      try {
        const cache = apolloClient.cache;
        const projectRef = identifyProject(cache, projectId);
        if (projectRef) {
          const typename = groupTypeName(args.type);
          const groupEntity = { __typename: typename, identifier: args.identifier, name: args.name, type: args.type || GroupType.Default, parentIdentifier: args.parentIdentifier ?? null };
          const groupRef = cache.writeFragment({ data: groupEntity, fragment: GROUP_ENTITY_FRAGMENTS[typename] });
          const orderedRef = cache.writeFragment({
            data: { __typename: 'orderedIdentifier' as const, identifier: args.identifier, index: nextOrderedIndex(cache, projectRef) },
            fragment: ORDERED_ENTITY_FRAGMENT,
          });
          appendToProjectState(cache, projectRef, { identifier: args.identifier, group: groupRef, ordered: orderedRef });
          for (const annotationId of args.annotationIdentifiers) {
            const annotationRef = cache.identify({ __typename: 'Annotation', identifier: annotationId } as never);
            if (annotationRef) cache.modify({ id: annotationRef, fields: { groupIdentifier: () => args.identifier } });
          }
          // Client-level no-op re-write → query broadcast with everything in place.
          apolloClient.writeFragment({ data: groupEntity, fragment: GROUP_ENTITY_FRAGMENTS[typename] });
        }
      } catch (err) {
        console.error('[createGroup] optimistic cache update failed:', err);
      }

      const mutation = createGroupMutation({
        variables: {
          projectId,
          identifier: args.identifier,
          name: args.name,
          type: args.type,
          annotationIdentifiers: args.annotationIdentifiers,
          parentIdentifier: args.parentIdentifier ?? undefined,
        },
      });
      // Protect the optimistic group + ordered entry from concurrent
      // full-state responses until this mutation's own response lands.
      trackPendingStateMutation({ created: [args.identifier] }, mutation);
      try {
        await mutation;
      } catch (err) {
        // Clear the phantom optimistic folder right away instead of waiting
        // for an unrelated full-state response to sweep it up.
        await refetchAnnotationState(projectId);
        throw err;
      }
    },
    [projectId, createGroupMutation, apolloClient, refetchAnnotationState],
  );

  const moveAnnotationsToGroup = useCallback(
    async (annotationIdentifiers: string[], groupIdentifier?: string | null) => {
      if (!projectId) return;

      // Optimistic cache update: update groupIdentifier on moved annotations
      const target = groupIdentifier || null;
      const cached = readCache(apolloClient, projectId);
      if (cached?.projectById) {
        const movedSet = new Set(annotationIdentifiers);
        writeCache(apolloClient, projectId, cached, (state) => ({
          annotations: (state.annotations || []).map((a) =>
            movedSet.has(a.identifier) ? { ...a, groupIdentifier: target } : a,
          ),
        }));
      }

      const mutation = moveAnnotationsToGroupMutation({
        variables: {
          projectId,
          annotationIdentifiers,
          groupIdentifier: groupIdentifier || undefined,
        },
      });
      // Protect the moved annotations' groupIdentifier from concurrent
      // full-state responses until this mutation's own response lands.
      trackPendingStateMutation({ moved: annotationIdentifiers.map((identifier) => ({ identifier, groupIdentifier: target })) }, mutation);
      try {
        await mutation;
      } catch (err) {
        await refetchAnnotationState(projectId);
        throw err;
      }
    },
    [projectId, moveAnnotationsToGroupMutation, apolloClient, refetchAnnotationState],
  );

  const ungroup = useCallback(
    async (groupIdentifier: string) => {
      if (!projectId) return;

      // Optimistic cache update mirroring the server's projectUngroup:
      // nested child groups cascade with the parent; direct children of a
      // ROOM folder re-attach to the parent floor (a sketch never escapes
      // its floor); everything else detaches to root. Never deletes
      // annotations.
      const cached = readCache(apolloClient, projectId);
      let removedIds: string[] = [groupIdentifier];
      let movedOptimistic: { identifier: string; groupIdentifier: string | null }[] = [];
      if (cached?.projectById) {
        const state = cached.projectById.state;
        const groups = state.groups || [];
        const targetGroup = groups.find((g) => g.identifier === groupIdentifier);
        removedIds = [groupIdentifier, ...groups.filter((g) => g.parentIdentifier === groupIdentifier).map((g) => g.identifier)];
        const removedSet = new Set(removedIds);
        const reattachTo = targetGroup?.type === GroupType.Room ? targetGroup.parentIdentifier ?? null : null;
        movedOptimistic = (state.annotations || [])
          .filter((a) => a.groupIdentifier && removedSet.has(a.groupIdentifier))
          .map((a) => ({ identifier: a.identifier, groupIdentifier: a.groupIdentifier === groupIdentifier ? reattachTo : null }));
        const movedById = new Map(movedOptimistic.map((m) => [m.identifier, m.groupIdentifier]));
        writeCache(apolloClient, projectId, cached, (state) => ({
          annotations: (state.annotations || []).map((a) => (movedById.has(a.identifier) ? { ...a, groupIdentifier: movedById.get(a.identifier) ?? null } : a)),
          groups: (state.groups || []).filter((g) => !removedSet.has(g.identifier)),
          orderedIdentifiers: (state.orderedIdentifiers || []).filter((o) => !removedSet.has(o.identifier)),
        }));
      }

      const mutation = ungroupMutation({
        variables: { projectId, identifier: groupIdentifier },
      });
      // `deleted` keeps the removed group rows from being resurrected by a
      // concurrent full-state response; `moved` protects the re-parented
      // children's groupIdentifier the same way.
      trackPendingStateMutation({ deleted: removedIds, moved: movedOptimistic }, mutation);
      try {
        await mutation;
      } catch (err) {
        await refetchAnnotationState(projectId);
        throw err;
      }
    },
    [projectId, ungroupMutation, apolloClient, refetchAnnotationState],
  );

  const updateOrdering = useCallback(
    async (args: { identifiers: { identifier: string; type?: string }[]; toIndex: number; toGroupIdentifier?: string | null }) => {
      if (!projectId) return;

      // No optimistic update for reordering — the drag overlay already provides
      // visual feedback, and computing correct indices client-side would duplicate
      // complex server logic. The mutation response updates the cache automatically.
      await updateOrderingMutation({
        variables: {
          projectId,
          identifiers: args.identifiers,
          toIndex: args.toIndex,
          toGroupIdentifier: args.toGroupIdentifier ?? null,
        },
      });
    },
    [projectId, updateOrderingMutation],
  );

  return { createGroup, moveAnnotationsToGroup, ungroup, updateOrdering };
}
